// Utilities
import { defineStore } from 'pinia';
import { createUid } from '@/utils/uid';
import type { NotificationColor, NotificationItem, NotificationState } from '@/types/shared/notification';

/** Gap between one toast closing and the next opening, so v-snackbar restarts
 *  its timeout on a fresh false→true transition (matches its leave animation). */
const ADVANCE_DELAY_MS = 200;

export const useNotificationStore = defineStore('notification', {
  state: (): NotificationState => ({
    current: null,
    queue: [],
    snackbar: false,
    closing: false
  }),
  actions: {
    /**
     * Enqueues a toast. Same signature as before — callers are unchanged. If
     * nothing is showing, it starts immediately; otherwise it waits its turn.
     */
    notify(text: string, color: NotificationColor, detail?: string): string {
      return this.show({ text, color, ...(detail ? { detail } : {}) });
    },
    /**
     * Enqueues a toast built by the caller, returning its id so a long-running
     * one can be updated or taken down once the work behind it finishes.
     */
    show(item: Omit<NotificationItem, 'id'>): string {
      const id = createUid();
      this.queue.push({ ...item, id });
      if (!this.snackbar) this.showNext();
      return id;
    },
    /** Patches a toast in place, whether it is on screen or still queued. */
    update(id: string, patch: Partial<Omit<NotificationItem, 'id'>>): void {
      if (this.current?.id === id) {
        this.current = { ...this.current, ...patch };
        return;
      }
      const index = this.queue.findIndex(item => item.id === id);
      if (index !== -1) this.queue[index] = { ...this.queue[index]!, ...patch };
    },
    /** Takes down a specific toast, ignoring it if it has already gone. */
    dismissById(id: string): void {
      if (this.current?.id === id) {
        this.dismiss();
        return;
      }
      this.queue = this.queue.filter(item => item.id !== id);
    },
    /** Pulls the next queued toast to the screen, or clears when the line ends. */
    showNext(): void {
      this.closing = false;
      const next = this.queue.shift();
      if (!next) {
        this.current = null;
        this.snackbar = false;
        return;
      }
      this.current = next;
      this.snackbar = true;
    },
    /**
     * Called when the current toast closes (timeout, close button, or the work
     * behind it finishing) and advances the queue. Re-entrant by design: the
     * close button and v-snackbar's own timeout can both land on one close.
     */
    dismiss(): void {
      if (this.closing) return;
      this.closing = true;
      this.snackbar = false;
      if (this.queue.length > 0) {
        setTimeout(() => this.showNext(), ADVANCE_DELAY_MS);
      } else {
        this.current = null;
        this.closing = false;
      }
    }
  }
});
